import React, { useState, useEffect } from 'react';
import { X, User, Phone, Mail, MapPin, Package, Calendar, FileText, Truck, Trash2, CheckCircle, AlertTriangle, Globe } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '../../../lib/supabase';
import { fatouReservationService } from '../../../services/fatouReservationService';

const STATUS_CONFIG = {
  pending: { label: 'En Attente', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  confirmed: { label: 'Confirmée', color: 'bg-green-100 text-green-700 border-green-200' },
  processing: { label: 'En Traitement', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  assigned: { label: 'Affectée', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  completed: { label: 'Terminée', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-700 border-red-200' },
};

const SOURCE_CONFIG = {
  whatsapp: { label: 'WhatsApp', color: 'bg-green-500', icon: '💬' },
  website: { label: 'Site Web', color: 'bg-blue-500', icon: '🌐' },
  fatou_ia: { label: 'Fatou IA', color: 'bg-purple-500', icon: '🤖' },
};

export default function ReservationDetailModal({ reservation, onClose, onUpdate, onDelete }) {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(reservation?.driver_id || '');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSuccess, setAssignSuccess] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    try {
      const { data, error } = await supabase
        ?.from('employees')
        ?.select('id, name, phone, role')
        ?.order('name');
      if (!error) setDrivers(data || []);
    } catch (err) {
      console.error('Error loading drivers:', err);
    }
  };

  const handleAssignDriver = async () => {
    if (!selectedDriverId) return;
    setAssignLoading(true);
    try {
      const driver = drivers?.find(d => d?.id === selectedDriverId);
      const updated = await fatouReservationService?.assignDriver(reservation?.id, selectedDriverId, driver?.name);
      setAssignSuccess(true);
      onUpdate?.(updated);
      setTimeout(() => setAssignSuccess(false), 3000);
    } catch (err) {
      console.error('Error assigning driver:', err);
      alert('Erreur lors de l\'affectation du chauffeur');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await fatouReservationService?.deleteReservation(reservation?.id);
      setDeleteSuccess(true);
      setTimeout(() => {
        onDelete?.(reservation?.id);
        onClose?.();
      }, 1200);
    } catch (err) {
      console.error('Error deleting reservation:', err);
      alert('Erreur lors de la suppression');
      setDeleteLoading(false);
    }
  };

  const statusCfg = STATUS_CONFIG?.[reservation?.status] || STATUS_CONFIG?.pending;
  const sourceCfg = SOURCE_CONFIG?.[reservation?.source] || SOURCE_CONFIG?.fatou_ia;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try { return format(new Date(dateStr), 'dd MMMM yyyy à HH:mm', { locale: fr }); }
    catch { return dateStr; }
  };

  const formatDateOnly = (dateStr) => {
    if (!dateStr) return '—';
    try { return format(new Date(dateStr), 'dd MMMM yyyy', { locale: fr }); }
    catch { return dateStr; }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={(e) => e?.target === e?.currentTarget && onClose?.()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Package className="text-green-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Détail de la Réservation</h2>
              <p className="text-xs text-gray-500">ID: {reservation?.id?.slice(0, 8)}...</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusCfg?.color}`}>
              {statusCfg?.label}
            </span>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Source & Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Calendar size={12} /> Date de création</p>
              <p className="text-sm font-semibold text-gray-800">{formatDate(reservation?.created_at)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Globe size={12} /> Source</p>
              <p className="text-sm font-semibold text-gray-800">
                <span className="mr-1">{sourceCfg?.icon}</span>{sourceCfg?.label}
              </p>
            </div>
          </div>

          {/* Client Info */}
          <div className="bg-blue-50 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-bold text-blue-800 flex items-center gap-2"><User size={16} /> Informations Client</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500">Nom</p>
                <p className="text-sm font-semibold text-gray-900">{reservation?.customer_name || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 flex items-center gap-1"><Phone size={10} /> Téléphone</p>
                <p className="text-sm font-semibold text-gray-900">{reservation?.customer_phone || '—'}</p>
              </div>
              {reservation?.customer_email && (
                <div className="sm:col-span-2">
                  <p className="text-xs text-gray-500 flex items-center gap-1"><Mail size={10} /> Email</p>
                  <p className="text-sm font-semibold text-gray-900">{reservation?.customer_email}</p>
                </div>
              )}
            </div>
          </div>

          {/* Addresses */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-orange-50 rounded-xl p-4">
              <h3 className="text-sm font-bold text-orange-800 flex items-center gap-2 mb-2"><MapPin size={16} /> Adresse de Collecte</h3>
              <p className="text-sm text-gray-800">{reservation?.pickup_address || '—'}</p>
              {reservation?.pickup_city && <p className="text-xs text-gray-500 mt-1">{reservation?.pickup_city} {reservation?.pickup_postal_code}</p>}
            </div>
            <div className="bg-green-50 rounded-xl p-4">
              <h3 className="text-sm font-bold text-green-800 flex items-center gap-2 mb-2"><MapPin size={16} /> Ville de Livraison</h3>
              <p className="text-sm text-gray-800">{reservation?.delivery_city || reservation?.delivery_address || '—'}</p>
              {reservation?.delivery_country && <p className="text-xs text-gray-500 mt-1">{reservation?.delivery_country}</p>}
            </div>
          </div>

          {/* Shipment Details */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><Package size={16} /> Détails de l'Envoi</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <p className="text-xs text-gray-500">Type de service</p>
                <p className="text-sm font-semibold text-gray-900">{reservation?.service_type || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Quantité</p>
                <p className="text-sm font-semibold text-gray-900">{reservation?.quantity || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Prix</p>
                <p className="text-sm font-semibold text-gray-900">{reservation?.price ? `${reservation?.price} €` : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Date collecte</p>
                <p className="text-sm font-semibold text-gray-900">{formatDateOnly(reservation?.collection_date)}</p>
              </div>
            </div>
            {reservation?.item_description && (
              <div>
                <p className="text-xs text-gray-500">Description des colis</p>
                <p className="text-sm text-gray-800 mt-1 bg-white rounded-lg p-2 border border-gray-200">{reservation?.item_description}</p>
              </div>
            )}
            {reservation?.special_instructions && (
              <div>
                <p className="text-xs text-gray-500 flex items-center gap-1"><FileText size={10} /> Notes / Instructions spéciales</p>
                <p className="text-sm text-gray-800 mt-1 bg-white rounded-lg p-2 border border-gray-200">{reservation?.special_instructions}</p>
              </div>
            )}
            {reservation?.admin_notes && (
              <div>
                <p className="text-xs text-gray-500">Notes admin</p>
                <p className="text-sm text-gray-800 mt-1 bg-yellow-50 rounded-lg p-2 border border-yellow-200">{reservation?.admin_notes}</p>
              </div>
            )}
          </div>

          {/* Driver Assignment */}
          <div className="bg-purple-50 rounded-xl p-4 space-y-3 border border-purple-100">
            <h3 className="text-sm font-bold text-purple-800 flex items-center gap-2"><Truck size={16} /> Affecter à un Chauffeur</h3>

            {reservation?.assigned_driver_name && (
              <div className="flex items-center gap-2 bg-purple-100 rounded-lg px-3 py-2">
                <CheckCircle size={16} className="text-purple-600" />
                <span className="text-sm font-semibold text-purple-800">Chauffeur actuel : {reservation?.assigned_driver_name}</span>
                {reservation?.assigned_at && (
                  <span className="text-xs text-purple-600 ml-auto">Affecté le {formatDateOnly(reservation?.assigned_at)}</span>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <select
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e?.target?.value)}
                className="flex-1 px-3 py-2 border border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-white"
              >
                <option value="">— Sélectionner un chauffeur —</option>
                {drivers?.map(driver => (
                  <option key={driver?.id} value={driver?.id}>
                    {driver?.name}{driver?.phone ? ` — ${driver?.phone}` : ''}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAssignDriver}
                disabled={!selectedDriverId || assignLoading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {assignLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Truck size={16} />
                )}
                Affecter
              </button>
            </div>

            {assignSuccess && (
              <div className="flex items-center gap-2 bg-green-100 text-green-700 rounded-lg px-3 py-2 text-sm">
                <CheckCircle size={16} />
                Chauffeur affecté avec succès ! Statut mis à jour : "Affectée"
              </div>
            )}
          </div>

          {/* Delete Section */}
          <div className="border border-red-200 rounded-xl p-4 bg-red-50">
            <h3 className="text-sm font-bold text-red-700 flex items-center gap-2 mb-3"><Trash2 size={16} /> Supprimer cette réservation</h3>

            {deleteSuccess ? (
              <div className="flex items-center gap-2 bg-green-100 text-green-700 rounded-lg px-3 py-2 text-sm">
                <CheckCircle size={16} />
                Réservation supprimée avec succès.
              </div>
            ) : !deleteConfirm ? (
              <button
                onClick={() => setDeleteConfirm(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 size={16} />
                Supprimer la réservation
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-2 bg-red-100 rounded-lg p-3">
                  <AlertTriangle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700 font-medium">Voulez-vous vraiment supprimer cette réservation ? Cette action est irréversible.</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleDelete}
                    disabled={deleteLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    {deleteLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Trash2 size={16} />}
                    Confirmer la suppression
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
